import type { SupabaseClient } from '@supabase/supabase-js';

// Single source of truth for DM unread counts, shared by BOTH the app-wide nav
// badge (DmRealtimeProvider) and the SSR home "Unread DMs" tile so the two agree
// by construction.
//
// Unread = messages in the user's active (non-archived) conversations, not
// authored by the user, that have no dm_read_receipts row for the user. Receipts
// are written by the mark_conversation_as_read RPC when a thread is opened. Three
// flat queries, no FK joins (matches the project rule).
//
// Archived-exclusion safety: a DB trigger (dm_unarchive_on_new_message) clears
// is_archived for every participant on any new message, in the same transaction
// as the insert. So no committed message can exist in a still-archived thread —
// excluding archived rows here can never hide a real unread.
export async function computeDmUnread(
  supabase: SupabaseClient<any, any, any>,
  userId: string,
): Promise<{ total: number; byConversation: Record<string, number> }> {
  // 1. Active conversations. Treat NULL is_archived as false so rows predating
  //    the column still count.
  const { data: parts } = await supabase
    .from('dm_participants')
    .select('conversation_id')
    .eq('user_id', userId)
    .not('is_archived', 'is', true);

  const ids = Array.from(
    new Set(
      ((parts ?? []) as Array<{ conversation_id: string }>)
        .map((p) => p.conversation_id)
        .filter(Boolean),
    ),
  );
  if (ids.length === 0) return { total: 0, byConversation: {} };

  // 2. Messages in those conversations NOT authored by the user. This
  //    sender-exclusion is the definitional guard — your own messages are never
  //    unread to you — and it holds regardless of receipt state, so the count
  //    stays correct even if a receipt write is ever missed. (Belt-and-braces:
  //    the dm_unarchive_on_new_message trigger also writes the sender a
  //    self-receipt on send, keeping receipt state truthful.)
  const { data: msgs } = await supabase
    .from('dm_messages')
    .select('id, conversation_id')
    .in('conversation_id', ids)
    .neq('sender_id', userId);

  const messages = (msgs ?? []) as Array<{ id: string; conversation_id: string }>;
  const byConversation: Record<string, number> = {};
  for (const id of ids) byConversation[id] = 0;
  if (messages.length === 0) return { total: 0, byConversation };

  // 3. Read-receipt rows for the user against those messages.
  const msgIds = messages.map((m) => m.id);
  const { data: receipts } = await supabase
    .from('dm_read_receipts')
    .select('message_id')
    .eq('user_id', userId)
    .in('message_id', msgIds);
  const readSet = new Set(
    ((receipts ?? []) as Array<{ message_id: string }>).map((r) => r.message_id),
  );

  let total = 0;
  for (const m of messages) {
    if (!readSet.has(m.id)) {
      byConversation[m.conversation_id] = (byConversation[m.conversation_id] || 0) + 1;
      total += 1;
    }
  }
  return { total, byConversation };
}
